import { ExternalVideoProvider } from '@/types/galleries'

export interface ParsedExternalVideo {
  provider: ExternalVideoProvider
  videoId: string
  /** Normalised URL (with scheme) that is sent to the API. */
  normalizedUrl: string
}

const YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
])
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'])
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/
const DIGITS = /^\d+$/

function toUrl(input: string): URL | null {
  const trimmed = input.trim()
  if (trimmed === '' || /\s/.test(trimmed)) return null
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(withScheme)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    return url
  } catch {
    return null
  }
}

function parseYouTube(url: URL): string | null {
  const host = url.hostname.toLowerCase()
  if (!YOUTUBE_HOSTS.has(host)) return null
  const segments = url.pathname.split('/').filter((s) => s !== '')

  if (host === 'youtu.be') {
    const id = segments[0]
    return segments.length === 1 && id !== undefined && YOUTUBE_ID.test(id) ? id : null
  }
  if (host.endsWith('youtube-nocookie.com')) {
    const id = segments[1]
    return segments.length === 2 && segments[0] === 'embed' && id !== undefined && YOUTUBE_ID.test(id) ? id : null
  }
  if (segments.length === 1 && segments[0] === 'watch') {
    const id = url.searchParams.get('v')
    return id !== null && YOUTUBE_ID.test(id) ? id : null
  }
  if (segments.length === 2 && (segments[0] === 'shorts' || segments[0] === 'embed')) {
    const id = segments[1]
    return id !== undefined && YOUTUBE_ID.test(id) ? id : null
  }
  return null
}

function parseVimeo(url: URL): string | null {
  const host = url.hostname.toLowerCase()
  if (!VIMEO_HOSTS.has(host)) return null
  const segments = url.pathname.split('/').filter((s) => s !== '')

  if (host === 'player.vimeo.com') {
    const id = segments[1]
    return segments.length === 2 && segments[0] === 'video' && id !== undefined && DIGITS.test(id) ? id : null
  }
  const first = segments[0]
  if (segments.length >= 1 && segments.length <= 2 && first !== undefined && DIGITS.test(first)) {
    return first
  }
  if (segments.length === 3 && segments[0] === 'channels') {
    const id = segments[2]
    return id !== undefined && DIGITS.test(id) ? id : null
  }
  return null
}

/** Mirrors the server-side parser: YouTube and Vimeo only, hosts matched exactly. */
export function parseExternalVideoUrl(input: string): ParsedExternalVideo | null {
  const url = toUrl(input)
  if (url === null) return null

  const youtubeId = parseYouTube(url)
  if (youtubeId !== null) {
    return { provider: ExternalVideoProvider.YouTube, videoId: youtubeId, normalizedUrl: url.toString() }
  }
  const vimeoId = parseVimeo(url)
  if (vimeoId !== null) {
    return { provider: ExternalVideoProvider.Vimeo, videoId: vimeoId, normalizedUrl: url.toString() }
  }
  return null
}
